import { defaultLeaveOf } from '@/constant/hr/leave-rule-catalog';
import { dateOnlyOf, orgWallClock } from '@/lib/utils/custom-date/custom-date';
import { workDaysOn } from '@/lib/utils/work-days/work-days';
import {
  LeaveSheetAllotment,
  LeaveSheetAttendance,
  LeaveSheetCategory,
  LeaveSheetInput,
  LeaveSheetLeave,
  LeaveSheetRow,
  LeaveSheetSummary,
  ToilPool,
} from '../types/leave-sheet.types';
import { rangesOverlap } from './date-range-overlap';
import { holidayDaysOf, leaveDaysOf } from './leave-days';

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Every calendar day ("YYYY-MM-DD") from `start` to `end`, both inclusive. Calendar-label arithmetic
 * in UTC: these days are kind A (hrm-timezone-standard) and never go through a zone conversion.
 */
const enumerateDays = (start: string, end: string): string[] => {
  const toMs = (day: string) => {
    const [year, month, date] = day.split('-').map(Number);
    return Date.UTC(year, month - 1, date);
  };
  const days: string[] = [];
  for (let ms = toMs(start), endMs = toMs(end); ms <= endMs; ms += DAY_MS) {
    days.push(new Date(ms).toISOString().slice(0, 10));
  }
  return days;
};

const weekdayNameOf = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number);
  return WEEKDAY_NAMES[new Date(Date.UTC(year, month - 1, date)).getUTCDay()];
};

/** The term's default leave (`defaultLeaveOf`: the category it marks `IsDefault`, or for a term
 * saved before that, the one the HR term form suggests for its employment type). `null` when it has
 * none. */
export const defaultAllotmentOf = (
  allotments: LeaveSheetAllotment[],
  employmentType?: string
): LeaveSheetAllotment | null => defaultLeaveOf(allotments, employmentType) ?? null;

/** A half-day leave (every request, legacy ones included, carries its `LeaveType`). */
const isHalfDayLeave = (leave: LeaveSheetLeave) => leave.LeaveType === 'HalfDay';

// The legacy half-day times (minutes of the local day): a clock-in at or after 12:00, a clock-out before 16:00.
const HALF_DAY_START_FROM_MINUTES = 12 * 60;
const HALF_DAY_END_BEFORE_MINUTES = 16 * 60;

/**
 * A completed day's shortfall, worked out from its times as the sheet is built (legacy half-day rule,
 * once the DAY_STATUS rule): `HalfDay` for a clock-in at or after 12:00 or a clock-out before 16:00 on the
 * same day, on the clock of the day's own `Timezone`; else `Absent` for clock time under half the
 * expected hours and `HalfDay` under them. '' for a full day, and for a day still open (it has no
 * clock-out to judge; a correction closes it).
 */
export const shortDayOf = (attendance: LeaveSheetAttendance): 'HalfDay' | 'Absent' | '' => {
  if (!attendance.StartTimeUtc || !attendance.EndTimeUtc || attendance.IsOpen) return '';
  const zone = attendance.Timezone || undefined;
  const start = orgWallClock(attendance.StartTimeUtc, zone);
  const end = orgWallClock(attendance.EndTimeUtc, zone);
  if (start.hour * 60 + start.minute >= HALF_DAY_START_FROM_MINUTES) return 'HalfDay';
  // A clock-out on a later day than the clock-in (a night shift) is never "before 16:00".
  const sameDay = start.year === end.year && start.month === end.month && start.day === end.day;
  if (sameDay && end.hour * 60 + end.minute < HALF_DAY_END_BEFORE_MINUTES) return 'HalfDay';
  const expected = attendance.ExpectedWorkingHourInSecond ?? 0;
  const clock = attendance.TotalClockTimeInSecond ?? 0;
  if (expected > 0 && clock < expected / 2) return 'Absent';
  if (expected > 0 && clock < expected) return 'HalfDay';
  return '';
};

/** Requests of the categories other than the default one, each counted now in its working days inside the
 * term (`leaveDaysOf`: the term's work days in force on each day, holidays skipped). */
const otherCategoriesOf = (input: LeaveSheetInput, defaultId?: string): LeaveSheetCategory[] => {
  const holidays = holidayDaysOf(input.holidays);
  const term = { start: input.periodStart, end: input.periodEnd };
  return input.allotments
    .filter((allotment) => allotment.IsApplicable && allotment.LeaveCategoryId !== defaultId)
    .map((allotment) => {
      const taken = input.leaves
        .filter(
          (leave) =>
            leave.LeaveCategoryId === allotment.LeaveCategoryId &&
            rangesOverlap(
              dateOnlyOf(leave.StartDate),
              dateOnlyOf(leave.EndDate),
              input.periodStart,
              input.periodEnd
            )
        )
        .reduce((sum, leave) => sum + leaveDaysOf(leave, input.workDays, holidays, term), 0);
      const allocated = allotment.LeaveLimitTotalDays ?? 0;
      return {
        LeaveCategoryId: allotment.LeaveCategoryId,
        LeaveCategory: allotment.LeaveCategory,
        IsUnlimited: Boolean(allotment.IsUnlimited),
        Allocated: allocated,
        Taken: taken,
        Remaining: allocated - taken,
      };
    });
};

/**
 * The legacy leave sheet (`GetUserLeaveHistoryForBtNpl` / `ForBd`), with the region switch replaced by
 * the employee's leave rules (`input.policy`). Walks the term from its first day to `lastDay`:
 *
 * 1. TOIL earned on the day (only under a `toilBalance` rule): adds to the balance and the TOIL pool
 *    (weekend or special under the split rule). Nothing else is looked at that day.
 * 2. A holiday or a weekday outside the HR term's `WorkDays` in force that day (`workDaysOn`): skipped.
 * 3. A default-category leave: takes 1 (0.5 for a half day) from the balance, and as much of it from
 *    the TOIL pool as is left (special pool for a leave marked special TOIL); a day the pool covers
 *    only in part is listed as two rows, its TOIL part and its allotment part. A full-day leave of
 *    any other category: the day is skipped.
 * 4. Missed attendance (only under the `missedAttendance` rule, flexible hours exempt): no attendance,
 *    or a day `shortDayOf` finds `Absent`, charges 1, or 0.5 next to a half-day leave; a `HalfDay`
 *    without leave charges 0.5; any other day with a `Pending` attendance correction charges like a day
 *    without attendance until the correction is approved.
 *
 * Pure: no network, no clock; every day is a plain "YYYY-MM-DD", and only a record's times are read on
 * the clock of its own `Timezone`.
 */
export const computeLeaveSheet = (
  input: LeaveSheetInput
): {
  summary: LeaveSheetSummary | null;
  rows: LeaveSheetRow[];
  categories: LeaveSheetCategory[];
} => {
  const defaultAllotment = defaultAllotmentOf(input.allotments, input.employmentType);
  const categories = otherCategoriesOf(input, defaultAllotment?.LeaveCategoryId);
  if (!defaultAllotment || !input.periodStart || !input.periodEnd) {
    return { summary: null, rows: [], categories };
  }

  const { policy } = input;
  const split = policy.toil === 'split';
  const withToil = policy.toil !== null;
  const chargesMissed =
    policy.deductMissedAttendance && !(policy.exemptFlexible && input.isFlexible);

  // Under the combined rule every TOIL is one pool, so both carry-forwards land in it.
  const weekendCarryForward = withToil ? input.toilCarryForwardDays : 0;
  const specialCarryForward = withToil ? input.specialToilCarryForwardDays : 0;
  // The default leave's allotment plus the annual leave carried into the term (LeaveCarryForwardDays).
  const leaveCarryForward = input.leaveCarryForwardDays ?? 0;
  const allocated = (defaultAllotment.LeaveLimitTotalDays ?? 0) + leaveCarryForward;

  let weekendLeft = weekendCarryForward;
  let specialLeft = specialCarryForward;
  let balance = allocated + weekendCarryForward + specialCarryForward;
  const toilLeft = () => weekendLeft + specialLeft;

  const rows: LeaveSheetRow[] = [];
  const push = (
    row: Omit<LeaveSheetRow, 'balance' | 'toilLeft' | 'weekendToilLeft' | 'specialToilLeft'>
  ) =>
    rows.push({
      ...row,
      balance,
      toilLeft: toilLeft(),
      weekendToilLeft: weekendLeft,
      specialToilLeft: specialLeft,
    });

  if (weekendCarryForward + specialCarryForward > 0) {
    push({
      date: '',
      kind: 'carryForward',
      leaveDays: 0,
      toilDays: weekendCarryForward + specialCarryForward,
      isHalfDay: false,
    });
  }

  /** How much of the next `days` of default leave the TOIL pool still covers. */
  const toilCovering = (days: number, fromSpecialToil: boolean) => {
    if (!withToil) return 0;
    if (split) return Math.min(fromSpecialToil ? specialLeft : weekendLeft, days);
    return Math.min(toilLeft(), days);
  };

  /** Takes `days` of default leave, using up TOIL first. */
  const take = (days: number, fromSpecialToil: boolean) => {
    balance -= days;
    if (!withToil) return;
    if (split && fromSpecialToil) specialLeft -= Math.min(specialLeft, days);
    else if (split) weekendLeft -= Math.min(weekendLeft, days);
    else {
      // One pool: weekend first, then whatever special carry-forward it holds.
      const fromWeekend = Math.min(weekendLeft, days);
      weekendLeft -= fromWeekend;
      specialLeft -= Math.min(specialLeft, days - fromWeekend);
    }
  };

  /**
   * Takes a day's `leaveDays` and pushes its row; a day the TOIL pool covers only in part is two rows
   * on the same date, the TOIL part (`fromToil`) first and then the part taken from the allotment.
   */
  const charge = (
    row: Omit<LeaveSheetRow, 'balance' | 'toilLeft' | 'weekendToilLeft' | 'specialToilLeft'>
  ) => {
    const fromToil = toilCovering(row.leaveDays, Boolean(row.fromSpecialToil));
    const parts =
      fromToil > 0 && fromToil < row.leaveDays
        ? [fromToil, row.leaveDays - fromToil]
        : [row.leaveDays];
    parts.forEach((leaveDays, index) => {
      take(leaveDays, Boolean(row.fromSpecialToil));
      push({ ...row, leaveDays, ...(parts.length > 1 && index === 0 ? { fromToil: true } : {}) });
    });
  };

  const holidayDays = new Set(input.holidays.map((holiday) => dateOnlyOf(holiday.StartDate)));
  const toilByDay = new Map<string, LeaveSheetInput['toils'][number]>();
  for (const toil of input.toils) {
    const day = dateOnlyOf(toil.ToilDate);
    if (day && !toilByDay.has(day)) toilByDay.set(day, toil);
  }
  const attendanceByDay = new Map(
    input.attendances.map((attendance) => [dateOnlyOf(attendance.AttendanceDate), attendance])
  );
  const pendingCorrectionDays = new Set(
    (input.pendingCorrections ?? []).map((correction) => dateOnlyOf(correction.CorrectionDate))
  );
  const leaves = input.leaves
    .map((leave) => ({ leave, start: dateOnlyOf(leave.StartDate), end: dateOnlyOf(leave.EndDate) }))
    .filter(({ start, end }) => start && end)
    // A day with two requests is read by the default-category one.
    .sort(
      (a, b) =>
        Number(b.leave.LeaveCategoryId === defaultAllotment.LeaveCategoryId) -
        Number(a.leave.LeaveCategoryId === defaultAllotment.LeaveCategoryId)
    );

  const lastDay = input.lastDay < input.periodEnd ? input.lastDay : input.periodEnd;
  const days = lastDay >= input.periodStart ? enumerateDays(input.periodStart, lastDay) : [];

  for (const day of days) {
    const toil = withToil ? toilByDay.get(day) : undefined;
    if (toil) {
      const toilDays = toil.ToilType === 'FullDay' ? 1 : 0.5;
      const pool: ToilPool = toil.ToilCategory === 'SpecialToil' ? 'SpecialToil' : 'WeekendToil';
      balance += toilDays;
      if (pool === 'SpecialToil' && split) specialLeft += toilDays;
      else weekendLeft += toilDays;
      push({
        date: day,
        kind: 'toil',
        leaveDays: 0,
        toilDays,
        toilPool: split ? pool : undefined,
        isHalfDay: toilDays < 1,
      });
      continue;
    }

    const isWorkday = workDaysOn(input.workDays, day).some(
      (workDay) => workDay.WorkDay === weekdayNameOf(day)
    );
    if (holidayDays.has(day) || !isWorkday) continue;

    const leave = leaves.find(({ start, end }) => start <= day && day <= end)?.leave;
    const halfDayLeave = leave ? isHalfDayLeave(leave) : false;
    if (leave && leave.LeaveCategoryId === defaultAllotment.LeaveCategoryId) {
      const leaveDays = halfDayLeave ? 0.5 : 1;
      const fromSpecialToil = split && leave.ToilCategory === 'SpecialToil';
      charge({
        date: day,
        kind: 'leave',
        leaveDays,
        toilDays: 0,
        title: leave.Title,
        isHalfDay: halfDayLeave,
        fromSpecialToil,
      });
    }
    // A full day of leave is fully accounted for; a half day still has a half to attend.
    if (leave && !halfDayLeave) continue;
    if (!chargesMissed) continue;

    const attendance = attendanceByDay.get(day);
    const shortDay = attendance ? shortDayOf(attendance) : '';
    let missed = 0;
    if (!attendance || shortDay === 'Absent') missed = leave ? 0.5 : 1;
    else if (shortDay === 'HalfDay') missed = leave ? 0 : 0.5;
    // Legacy: a day under a correction request counts as missed until the correction is approved.
    else if (pendingCorrectionDays.has(day)) missed = leave ? 0.5 : 1;
    if (missed > 0) {
      charge({
        date: day,
        kind: 'missedAttendance',
        leaveDays: missed,
        toilDays: 0,
        isHalfDay: missed < 1,
      });
    }
  }

  const sum = (pick: (row: LeaveSheetRow) => number, keep: (row: LeaveSheetRow) => boolean) =>
    rows.filter(keep).reduce((total, row) => total + pick(row), 0);
  const isToil = (row: LeaveSheetRow) => row.kind === 'toil';
  const weekendToilEarned = sum(
    (row) => row.toilDays,
    (row) => isToil(row) && row.toilPool !== 'SpecialToil'
  );
  const specialToilEarned = sum(
    (row) => row.toilDays,
    (row) => isToil(row) && row.toilPool === 'SpecialToil'
  );

  const summary: LeaveSheetSummary = {
    defaultCategory: defaultAllotment.LeaveCategory,
    allocated,
    leaveCarryForward,
    taken: sum(
      (row) => row.leaveDays,
      (row) => row.kind === 'leave' || row.kind === 'missedAttendance'
    ),
    balance,
    remainingLeave: balance - toilLeft(),
    toilCarryForward: weekendCarryForward + specialCarryForward,
    weekendToilCarryForward: weekendCarryForward,
    specialToilCarryForward: specialCarryForward,
    toilEarned: weekendToilEarned + specialToilEarned,
    weekendToilEarned,
    specialToilEarned,
    weekendToilTaken: weekendCarryForward + weekendToilEarned - weekendLeft,
    specialToilTaken: specialCarryForward + specialToilEarned - specialLeft,
    toilLeft: toilLeft(),
    weekendToilLeft: weekendLeft,
    specialToilLeft: specialLeft,
  };

  return { summary, rows, categories };
};
